import { autoUpdate, flip, FloatingTree, offset, Placement, shift, size, useFloating } from '@floating-ui/react';
import clsx from 'clsx';
import { MapMouseEvent } from 'maplibre-gl';
import React, { ReactNode } from 'react';
import useMap from '../../../app/hooks/use-map';
import useMapInteraction from '../../../hooks/use-map-interaction';
import { useSafeEffect } from '../../../app/hooks/use-safe-effect';

export function MapTooltip(props: { children?: ReactNode; position: MapMouseEvent; placement?: Placement; scrollable?: boolean }) {
    const mapInteractionService = useMapInteraction();
    const [open, setOpen] = React.useState(false);
    const map = useMap();

    const { refs, floatingStyles, context } = useFloating({
        open: open,
        placement: props.placement ?? 'bottom',
        whileElementsMounted: autoUpdate,
        middleware: [
            offset({ crossAxis: 10, mainAxis: 16 }),
            flip({ mainAxis: true, crossAxis: true, boundary: map?._container, padding: 10 }),
            shift({ mainAxis: true, crossAxis: false, boundary: map?._container, padding: 10 }),

            props.scrollable &&
                size({
                    padding: 15,
                    apply(p) {
                        p.elements.floating.style.maxHeight = Math.max(0, p.availableHeight) + 'px';
                    }
                })
        ]
    });

    const floating = context.elements.floating;
    const overflow = floating && floating.scrollHeight > floating.clientHeight;

    React.useEffect(() => {
        const virtualElement = {
            getBoundingClientRect: () => ({
                x: props.position.originalEvent.clientX,
                y: props.position.originalEvent.clientY,
                top: props.position.originalEvent.clientY,
                bottom: props.position.originalEvent.clientY,
                left: props.position.originalEvent.clientX,
                right: props.position.originalEvent.clientX,
                width: 5,
                height: 5
            })
        };
        refs.setReference(virtualElement);
        setOpen(true);
    }, [refs, props.position]);

    const handleMouseLeave = React.useCallback(() => {
        mapInteractionService.dismissTooltip();
    }, [mapInteractionService]);

    const wasOverflowingRef = React.useRef(overflow);
    useSafeEffect(() => {
        if (wasOverflowingRef.current && !overflow) {
            mapInteractionService.dismissTooltip();
        }
        wasOverflowingRef.current = overflow;
    }, [overflow, mapInteractionService]);

    useSafeEffect(() => {
        return () => {
            mapInteractionService.dismissTooltip();
        };
    }, [mapInteractionService]);

    return (
        <FloatingTree>
            {open && (
                <div
                    data-scrollable={overflow && (props.scrollable ?? false)}
                    // If we're allowed to scroll the content the overflow MUST be applied to this container. If we try to scroll container deeper in this tree it'll have a lot of side effects
                    // When the overflow is applied somewhere deeper in the tree it's impossible to know the size of the container which is important for positioning
                    // So without getting too complicated we just allow weather tooltip to be scrolled. In that case don't apply k-card to weather tooltip content but to floating element here
                    className={clsx('map-tooltip', props.scrollable && 'k-card')}
                    onMouseLeave={handleMouseLeave}
                    ref={refs.setFloating}
                    style={{
                        ...floatingStyles,
                        zIndex: 333,
                        overflowY: overflow ? 'scroll' : 'hidden',
                        pointerEvents: overflow ? 'auto' : 'none'
                    }}>
                    {props.children}
                </div>
            )}
        </FloatingTree>
    );
}
